import { useContext } from "react";
import { useSelector } from "react-redux";
import { Boundaries } from "@planzone/core";
import {
  CapacityGranularity,
  CapacitySort,
  Filter,
  ResourceWorkload,
} from "@planzone/dto";
import { CoreContext } from "../context";

interface ViewModel {
  isRequestPending: boolean;
  isRequestSuccess: boolean;
  isRequestFailure: boolean;
  content: ResourceWorkload[];
  currentPage: number;
  canLoadMore: boolean;
  totalElements: number;
  retrieveWorkload(request: Boundaries["retrieveWorkload"]["request"]): void;
}

function useRetrieveWorkload(
  from: string,
  to: string,
  granularity: CapacityGranularity,
  filter: Filter | undefined,
  sort: CapacitySort | undefined,
  size: number,
): ViewModel {
  const { selector, core } = useContext(CoreContext);

  return {
    isRequestPending: useSelector(() =>
      selector.isRetrieveWorkloadRequestPending(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    isRequestSuccess: useSelector(() =>
      selector.isRetrieveWorkloadRequestSuccess(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    isRequestFailure: useSelector(() =>
      selector.isRetrieveWorkloadRequestFailure(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    content: useSelector(() =>
      selector.getWorkloadContent(from, to, granularity, filter, sort, size),
    ),
    currentPage: useSelector(() =>
      selector.getWorkloadCurrentPage(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    canLoadMore: useSelector(() =>
      selector.canLoadMoreWorkload(from, to, granularity, filter, sort, size),
    ),
    totalElements: useSelector(() =>
      selector.getWorkloadTotalElements(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    retrieveWorkload: core.retrieveWorkload,
  };
}

export { useRetrieveWorkload };
